import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';

function PaymentApproval() {
    const [payments, setPayments] = useState<any>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [rateType, setRateType] = useState('all');
    const [paymentType, setPaymentType] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Modals
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectId, setRejectId] = useState<number | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [balanceModal, setBalanceModal] = useState(false);
    const [maxAmount, setMaxAmount] = useState('');
    const [filterApproveModal, setFilterApproveModal] = useState(false);
    const [fineDetailModal, setFineDetailModal] = useState(false);
    const [selectedFineBreakdown, setSelectedFineBreakdown] = useState<any>(null);

    // ── Load payments ──────────────────────────────────────────
    const loadPayments = useCallback((page?: string) => {
        setLoader(true);
        const query: any = {
            search, rate_type: rateType, payment_type: paymentType,
            date_from: dateFrom, date_to: dateTo,
            page: page || '1', per_page: '20',
        };

        SuperAdminApis.getConfirmedPayments(query).then(
            (response: AxiosResponse<any>) => {
                setLoader(false);
                if (response?.data?.status) {
                    setPayments(response.data.data);
                    setSummary(response.data.summary);
                }
            }
        ).catch(() => { setLoader(false); toast.error('Failed to load payments.'); });
    }, [search, rateType, paymentType, dateFrom, dateTo]);

    useEffect(() => { loadPayments(); }, []);

    // ── Select handling ────────────────────────────────────────
    const toggleSelect = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };
    const toggleSelectAll = () => {
        if (selectAll) { setSelectedIds([]); } else { setSelectedIds(payments?.data?.map((p: any) => p.id) || []); }
        setSelectAll(!selectAll);
    };

    // ── Single approve ─────────────────────────────────────────
    const approveSingle = (id: number) => {
        setActionLoader(true);
        SuperAdminApis.approvePayment(id).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) { toast.success('Payment approved and wallet credited.'); loadPayments(); }
            else { toast.error(res?.data?.message || 'Failed.'); }
        }).catch((err: any) => { setActionLoader(false); toast.error(err?.response?.data?.message || 'Approval failed.'); });
    };

    // ── Bulk approve ───────────────────────────────────────────
    const bulkApprove = () => {
        if (selectedIds.length === 0) { toast.warn('Select payments first.'); return; }
        setActionLoader(true);
        SuperAdminApis.bulkApprovePayments({ payment_ids: selectedIds }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message);
                setSelectedIds([]); setSelectAll(false); loadPayments();
            }
        }).catch(() => { setActionLoader(false); toast.error('Bulk approve failed.'); });
    };

    // ── Approve by balance ─────────────────────────────────────
    const handleApproveByBalance = () => {
        if (!maxAmount || Number(maxAmount) <= 0) { toast.warn('Enter a valid amount.'); return; }
        setActionLoader(true);
        SuperAdminApis.approvePaymentsByBalance({ max_amount: Number(maxAmount) }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            setBalanceModal(false);
            if (res?.data?.status) {
                toast.success(res.data.message);
                setMaxAmount(''); loadPayments();
            }
        }).catch(() => { setActionLoader(false); toast.error('Approve by balance failed.'); });
    };

    // ── Filter approve ─────────────────────────────────────────
    const handleFilterApprove = () => {
        setActionLoader(true);
        const data: any = {
            rate_type: rateType !== 'all' ? rateType : undefined,
            payment_type: paymentType !== 'all' ? paymentType : undefined,
            date_from: dateFrom || undefined, date_to: dateTo || undefined,
            search: search || undefined,
        };
        SuperAdminApis.filterApprovePayments(data).then((res: AxiosResponse<any>) => {
            setActionLoader(false); setFilterApproveModal(false);
            if (res?.data?.status) { toast.success(res.data.message); loadPayments(); }
        }).catch(() => { setActionLoader(false); toast.error('Filter approve failed.'); });
    };

    // ── Reject ─────────────────────────────────────────────────
    const handleReject = () => {
        if (!rejectReason || !rejectId) { toast.warn('Reason required.'); return; }
        setActionLoader(true);
        SuperAdminApis.rejectPayment(rejectId, { reason: rejectReason }).then((res: AxiosResponse<any>) => {
            setActionLoader(false); setRejectModal(false); setRejectReason('');
            if (res?.data?.status) { toast.success('Payment rejected.'); loadPayments(); }
        }).catch(() => { setActionLoader(false); toast.error('Reject failed.'); });
    };

    const paginate = (label: string) => {
        const page = label === 'Next &raquo;' || label === '&laquo; Previous'
            ? payments?.links?.find((l: any) => l.label === label)?.url?.split('page=')[1] : label;
        if (page) loadPayments(page);
    };

    const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

    const showFineDetail = (breakdown: any) => {
        setSelectedFineBreakdown(breakdown);
        setFineDetailModal(true);
    };

    // ── NEW: Calculate page-level fine totals ──────────────────
    const pageTotalGross = payments?.data?.reduce((s: number, p: any) => s + Number(p.gross_amount ?? p.amount ?? 0), 0) || 0;
    const pageTotalFines = payments?.data?.reduce((s: number, p: any) => s + Number(p.fine_amount ?? 0), 0) || 0;
    const pageTotalNet = payments?.data?.reduce((s: number, p: any) => s + Number(p.amount ?? 0), 0) || 0;

    return (
        <>
            <AdminSidebar title="Payment Approval" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Payment Approval" />

                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <h3 className="text-[15px] text-[#1C1C1C] font-semibold mt-5 mb-2">Payment Approval</h3>
                    <p className="text-xs text-gray-500 mb-4">Approve confirmed payments to credit rider/fleet wallets.</p>

                    {/* ── Summary Cards — UPDATED with fine totals ──── */}
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Awaiting Approval</p>
                                <p className="text-xl font-bold text-gray-800">{summary.total_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.total_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Metro</p>
                                <p className="text-xl font-bold text-blue-600">{summary.metro_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.metro_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Other</p>
                                <p className="text-xl font-bold text-orange-600">{summary.other_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.other_amount)}</p>
                            </div>
                            {/* NEW: Gross / Fine / Net summary for current page */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Page Gross</p>
                                <p className="text-xl font-bold text-gray-800">{formatAmount(pageTotalGross)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Page Fines</p>
                                <p className="text-xl font-bold text-red-600">-{formatAmount(pageTotalFines)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Page Net</p>
                                <p className="text-xl font-bold text-green-700">{formatAmount(pageTotalNet)}</p>
                                <p className="text-xs text-gray-400">{selectedIds.length} selected</p>
                            </div>
                        </div>
                    )}

                    {/* ── Filters ───────────────────────────────────── */}
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[150px]">
                                <label className="text-xs text-gray-500">Search</label>
                                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Shipment, rider..." className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[120px]">
                                <label className="text-xs text-gray-500">Rate Type</label>
                                <select value={rateType} onChange={(e) => setRateType(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option><option value="metro">Metro</option><option value="other">Other</option>
                                </select>
                            </div>
                            <div className="min-w-[120px]">
                                <label className="text-xs text-gray-500">Type</label>
                                <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option><option value="fleet">Fleet</option><option value="independent">Independent</option>
                                </select>
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">From</label>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">To</label>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <button onClick={() => loadPayments()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
                        </div>
                    </div>

                    {/* ── Bulk Actions ──────────────────────────────── */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        <button onClick={bulkApprove} disabled={selectedIds.length === 0 || actionLoader} className="bg-green-600 text-white px-4 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50 flex items-center gap-1">
                            ✓ Approve Selected ({selectedIds.length})
                            <Oval visible={actionLoader} height="14" width="14" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                        </button>
                        <button onClick={() => setBalanceModal(true)} className="bg-purple-600 text-white px-4 py-1.5 rounded-md text-xs font-semibold">
                            💰 Approve by Balance
                        </button>
                        <button onClick={() => setFilterApproveModal(true)} className="bg-blue-600 text-white px-4 py-1.5 rounded-md text-xs font-semibold">
                            ✓ Approve All Matching Filter
                        </button>
                    </div>

                    {/* ── Table — UPDATED with fine columns ────────── */}
                    <div className="relative overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                            <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                <tr>
                                    <th className="px-2 py-2"><input type="checkbox" checked={selectAll} onChange={toggleSelectAll} /></th>
                                    <th className="px-2 py-2">Rider</th>
                                    <th className="px-2 py-2">Fleet</th>
                                    <th className="px-2 py-2">Shipment</th>
                                    <th className="px-2 py-2">Gross Rate</th>
                                    <th className="px-2 py-2">Fine</th>
                                    <th className="px-2 py-2">Net Amount</th>
                                    <th className="px-2 py-2">Rate</th>
                                    <th className="px-2 py-2">Confirmed By</th>
                                    <th className="px-2 py-2">Confirmed At</th>
                                    <th className="px-4 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments?.data?.length > 0 ? payments.data.map((p: any) => (
                                    <tr key={p.id} className="bg-white">
                                        <td className="px-2 py-3"><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} /></td>
                                        <td className="px-2 py-3">
                                            <div className="text-xs font-semibold">{p.rider?.first_name} {p.rider?.last_name}</div>
                                            <div className="text-[10px] text-gray-400">{p.rider?.email}</div>
                                        </td>
                                        <td className="px-2 py-3 text-xs">{p.fleet?.first_name || <span className="text-gray-300">—</span>}</td>
                                        <td className="px-2 py-3 text-xs font-mono">{p.shipment_number}</td>
                                        {/* UPDATED: Gross Rate */}
                                        <td className="px-2 py-3 text-xs text-gray-600">
                                            {formatAmount(p.gross_amount ?? p.amount)}
                                        </td>
                                        {/* NEW: Fine column */}
                                        <td className="px-2 py-3 text-xs">
                                            {Number(p.fine_amount || 0) > 0 ? (
                                                <span
                                                    className="text-red-600 font-semibold cursor-pointer hover:underline"
                                                    onClick={() => p.fine_breakdown && showFineDetail(p.fine_breakdown)}
                                                >
                                                    -{formatAmount(p.fine_amount)}
                                                    <span className="block text-[10px] text-gray-400 font-normal">
                                                        {p.fine_days} {p.fine_days === 1 ? 'day' : 'days'} late
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        {/* UPDATED: Net Amount */}
                                        <td className="px-2 py-3 text-xs font-semibold text-gray-800">
                                            {formatAmount(p.amount)}
                                            {Number(p.fine_amount || 0) > 0 && (
                                                <span className="block text-[10px] text-orange-500 font-normal">after fine</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-3">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${p.rate_type === 'metro' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{p.rate_type}</span>
                                        </td>
                                        <td className="px-2 py-3 text-xs">{p.confirmer?.first_name} {p.confirmer?.last_name}</td>
                                        <td className="px-2 py-3 text-xs">{p.confirmed_at ? new Date(p.confirmed_at).toLocaleString() : '—'}</td>
                                        <td className="px-4 py-3 flex gap-2">
                                            <button onClick={() => approveSingle(p.id)} className="text-green-600 hover:underline text-xs font-semibold">Approve</button>
                                            <button onClick={() => { setRejectId(p.id); setRejectModal(true); }} className="text-red-600 hover:underline text-xs font-semibold">Reject</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={11} className="text-center py-8 text-gray-400">No confirmed payments awaiting approval.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-end mt-4">
                        {payments?.links?.map((link: any, idx: number) => (
                            <button key={idx} onClick={() => paginate(link.label)} disabled={link.active || !link.url}
                                className={`mx-0.5 py-1 px-2 text-xs ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black' : 'bg-gray-100 text-gray-400'}`}>
                                {link.label.replace('&laquo; Previous', '< Prev').replace('Next &raquo;', 'Next >')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Reject Modal */}
            <Modal visible={rejectModal} width="400" height="250" effect="fadeInUp" onClickAway={() => setRejectModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Reject Payment</p>
                        <p className="cursor-pointer" onClick={() => setRejectModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm mb-4" rows={3} placeholder="Reason for rejection..." />
                    <button onClick={handleReject} disabled={actionLoader} className="bg-red-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Reject <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* Approve by Balance Modal */}
            <Modal visible={balanceModal} width="400" height="250" effect="fadeInUp" onClickAway={() => setBalanceModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Approve by Balance</p>
                        <p className="cursor-pointer" onClick={() => setBalanceModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Enter the maximum amount to approve. Oldest payments are processed first.</p>
                    <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Max Amount (₦)</label>
                        <input type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} className="w-full border border-gray-300 rounded-md p-2.5 text-sm" placeholder="e.g., 500000" />
                    </div>
                    <button onClick={handleApproveByBalance} disabled={actionLoader} className="bg-purple-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Approve up to {maxAmount ? formatAmount(maxAmount) : '₦0'}
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* Filter Approve Modal */}
            <Modal visible={filterApproveModal} width="400" height="200" effect="fadeInUp" onClickAway={() => setFilterApproveModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Approve All Matching</p>
                        <p className="cursor-pointer" onClick={() => setFilterApproveModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                        This will approve <strong>all</strong> confirmed payments matching your current filters ({summary?.total_count || 0} payments, {formatAmount(summary?.total_amount)}) and credit their wallets.
                    </p>
                    <button onClick={handleFilterApprove} disabled={actionLoader} className="bg-green-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Approve All & Credit Wallets ({summary?.total_count || 0})
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* NEW: Fine Detail Modal */}
            <Modal visible={fineDetailModal} width="420" height="320" effect="fadeInUp" onClickAway={() => setFineDetailModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Late Delivery Fine Breakdown</p>
                        <p className="cursor-pointer" onClick={() => setFineDetailModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    {selectedFineBreakdown && (
                        <div className="space-y-2 text-sm text-gray-600">
                            <div className="grid grid-cols-2 gap-2">
                                <div><p className="text-[10px] text-gray-400 uppercase">OFD Time</p><p className="text-xs">{selectedFineBreakdown.ofd_at ? new Date(selectedFineBreakdown.ofd_at).toLocaleString() : '—'}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">POD Time</p><p className="text-xs">{selectedFineBreakdown.pod_at ? new Date(selectedFineBreakdown.pod_at).toLocaleString() : '—'}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Total Hours</p><p className="text-xs">{selectedFineBreakdown.hours_elapsed}hrs</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Grace Period</p><p className="text-xs">{selectedFineBreakdown.grace_period_hours}hrs</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Hours Late</p><p className="text-xs text-red-600 font-semibold">{selectedFineBreakdown.hours_late}hrs</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Late Days</p><p className="text-xs text-red-600 font-semibold">{selectedFineBreakdown.late_days}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Fine Rate</p><p className="text-xs">{formatAmount(selectedFineBreakdown.base_fine_rate)}/day</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Capped?</p><p className="text-xs">{selectedFineBreakdown.capped ? 'Yes (at commission)' : 'No'}</p></div>
                            </div>
                            <div className="border-t pt-2 mt-2 space-y-1">
                                <div className="flex justify-between"><span className="text-xs text-gray-500">Gross Commission</span><span className="text-xs font-semibold">{formatAmount(selectedFineBreakdown.gross_amount)}</span></div>
                                <div className="flex justify-between"><span className="text-xs text-red-600">Fine Deducted</span><span className="text-xs font-semibold text-red-600">-{formatAmount(selectedFineBreakdown.fine_amount)}</span></div>
                                <div className="flex justify-between border-t pt-1"><span className="text-xs text-gray-800 font-bold">Net Amount</span><span className="text-xs font-bold text-green-700">{formatAmount(selectedFineBreakdown.net_amount)}</span></div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default PaymentApproval;